import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles`);
        const found = response.data.find(a => a.slug === slug);
        setArticle(found);
      } catch (error) {
        console.error('Failed to fetch article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Convert markdown-style content to HTML
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let i = 0;
    
    while (i < lines.length) {
      const line = lines[i];
      
      // Detect markdown table (line with | characters)
      if (line.trim().startsWith('|') && line.trim().endsWith('|') && line.includes('|')) {
        // Collect all table lines
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        
        if (tableLines.length >= 2) {
          // Parse header row
          const headerCells = tableLines[0].split('|').filter(c => c.trim() !== '');
          // Skip separator row (|---|---|)
          const startRow = tableLines[1].includes('---') ? 2 : 1;
          // Parse data rows
          const dataRows = tableLines.slice(startRow).map(row => 
            row.split('|').filter(c => c.trim() !== '').map(c => c.trim())
          );
          
          elements.push(
            <div key={`table-${i}`} className="my-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#1A1A1A]">
                    {headerCells.map((cell, j) => (
                      <th key={j} className="px-4 py-3 text-left text-white font-semibold border border-gray-700 whitespace-nowrap">
                        {cell.trim().split(/\*\*(.*?)\*\*/g).map((part, k) => 
                          k % 2 === 1 ? <strong key={k}>{part}</strong> : part
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, j) => (
                    <tr key={j} className={j % 2 === 0 ? 'bg-[#FAF9F6]' : 'bg-white'}>
                      {row.map((cell, k) => (
                        <td key={k} className="px-4 py-2.5 border border-gray-200 text-gray-700 whitespace-nowrap">
                          {cell.split(/\*\*(.*?)\*\*/g).map((part, m) => 
                            m % 2 === 1 ? <strong key={m}>{part}</strong> : part
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }
      
      // Headers
      if (line.startsWith('# ')) {
        elements.push(<h1 key={i} className="font-display text-3xl md:text-4xl mt-8 mb-6">{line.slice(2)}</h1>);
        i++; continue;
      }
      if (line.startsWith('## ')) {
        elements.push(<h2 key={i} className="font-display text-2xl mt-8 mb-4">{line.slice(3)}</h2>);
        i++; continue;
      }
      if (line.startsWith('### ')) {
        elements.push(<h3 key={i} className="font-display text-xl mt-6 mb-3">{line.slice(4)}</h3>);
        i++; continue;
      }
      
      // Bold text
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        elements.push(
          <p key={i} className="text-gray-700 leading-relaxed mb-4">
            {parts.map((part, j) => 
              j % 2 === 1 ? <strong key={j}>{part}</strong> : part
            )}
          </p>
        );
        i++; continue;
      }
      
      // List items
      if (line.startsWith('- ')) {
        elements.push(
          <li key={i} className="text-gray-700 ml-6 mb-2 list-disc">
            {line.slice(2)}
          </li>
        );
        i++; continue;
      }
      
      // Numbered list
      if (/^\d+\.\s/.test(line)) {
        elements.push(
          <li key={i} className="text-gray-700 ml-6 mb-2 list-decimal">
            {line.replace(/^\d+\.\s/, '')}
          </li>
        );
        i++; continue;
      }
      
      // Empty lines
      if (line.trim() === '') {
        elements.push(<div key={i} className="h-4" />);
        i++; continue;
      }
      
      // Regular paragraphs
      elements.push(<p key={i} className="text-gray-700 leading-relaxed mb-4">{line}</p>);
      i++;
    }
    
    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B8860B]"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl mb-4">Article not found</h1>
          <Link to="/insights" className="text-[#B8860B] hover:underline">
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "author": {
      "@type": "Organization",
      "name": article.author,
      "url": "https://magnuskor.com"
    },
    "datePublished": article.created_at,
    "dateModified": article.created_at,
    "publisher": {
      "@type": "Organization",
      "name": "Magnus Kor Talent Partners",
      "logo": {
        "@type": "ImageObject",
        "url": "https://magnuskor.com/logo512.png",
        "width": 512,
        "height": 512
      },
      "url": "https://magnuskor.com"
    },
    "image": article.image_url,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://magnuskor.com/insights/${article.slug}`
    },
    "articleSection": article.category,
    "inLanguage": "en-US",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Magnus Kor Talent Partners",
      "url": "https://magnuskor.com"
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title={article.title}
        description={article.excerpt}
        keywords={`${article.category}, startup hiring, talent advisory, ${article.title.toLowerCase().split(' ').slice(0, 3).join(', ')}`}
        canonical={`https://magnuskor.com/insights/${article.slug}`}
        ogType="article"
        jsonLd={articleSchema}
        article={{ publishedTime: article.created_at, section: article.category }}
      />

      {/* Hero */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <Link 
            to="/insights" 
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#B8860B] transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to Insights
          </Link>
          
          <span className="inline-block text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-4">
            {article.category}
          </span>
          
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-6">
            {article.title}
          </h1>
          
          <p className="text-lg text-gray-600 mb-8">
            {article.excerpt}
          </p>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <User size={16} />
              {article.author}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} />
              {article.read_time}
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {new Date(article.created_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <img loading="lazy" 
            src={article.image_url} 
            alt={article.title}
            className="w-full h-64 md:h-96 object-cover"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <article className="prose prose-lg max-w-none">
            {renderContent(article.content)}
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
            Ready to build your team?
          </h2>
          <p className="text-gray-400 mb-8">
            Let's talk about your hiring needs.
          </p>
          <Link 
            to="/for-startups" 
            className="inline-flex items-center gap-2 bg-[#B8860B] text-white px-8 py-3 font-medium hover:bg-[#9A7209] transition-colors"
          >
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ArticlePage;
